import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FloatingShapes from '@/components/FloatingShapes';
import ComparisonDiagram from '@/components/ComparisonDiagram';

export default function GenericVsCustomSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-14 md:py-20 bg-background-100 overflow-hidden">
      {/* Floating shapes */}
      <FloatingShapes count={16} color="accent" />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={12} color="secondary" />
      </div>

      {/* Gradient orbs */}
      <div className="absolute top-20 left-[10%] w-72 h-72 bg-accent-500/6 rounded-full blur-3xl animate-[morphPulse_6s_ease-in-out_infinite]" />
      <div className="absolute bottom-20 right-[10%] w-80 h-80 bg-secondary-500/5 rounded-full blur-3xl animate-[morphFloat_8s_ease-in-out_infinite_1s]" />

      {/* Decorative moving elements */}
      <div className="absolute top-32 left-[20%] w-6 h-6 border border-accent-300/20 rounded-full animate-[morphFloat_7s_ease-in-out_infinite]" />
      <div className="absolute bottom-40 right-[25%] w-10 h-10 border border-secondary-300/20 rounded-lg rotate-12 animate-[morphPulse_5s_ease-in-out_infinite_2s]" />

      <div className="relative w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div
            className={`text-center mb-16 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="inline-block px-3 py-1 bg-accent-100/80 rounded-full text-[10px] font-mono font-medium text-accent-700 uppercase tracking-widest mb-4">
              {t('home.generic.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 leading-tight tracking-tight max-w-3xl mx-auto">
              {t('home.generic.title1')}{' '}
              <span className="text-accent-500">{t('home.generic.title2')}</span>
            </h2>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              {t('home.generic.paragraph')}
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
            {/* Left: Animated image */}
            <div
              className={`lg:col-span-2 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
              style={{ transitionDelay: '0.2s' }}
            >
              <div className="relative rounded-xl overflow-hidden group sticky top-32">
                <div className="aspect-[3/4] relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=Layered%20neural%20network%20patterns%20with%20glowing%20nodes%20and%20connections%2C%20deep%20navy%20blue%20and%20electric%20violet%20gradients%2C%20abstract%20data%20visualization%2C%20enterprise%20technology%20aesthetic%2C%20clean%20modern%20style%2C%20high%20detail%2C%20dark%20background%20with%20illuminated%20data%20pathways&width=600&height=800&seq=ai-neural-custom-v2&orientation=portrait"
                    alt="Custom AI neural architecture"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-500/30 via-transparent to-transparent" />
                </div>

                {/* Floating badges on image */}
                <div className="absolute top-4 left-4 px-3 py-1.5 bg-background-50/90 backdrop-blur-sm rounded-md border border-background-200/60">
                  <span className="text-[10px] font-mono text-accent-600 uppercase tracking-wider">{t('home.generic.badgeNeural')}</span>
                </div>
                <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-accent-500/90 backdrop-blur-sm rounded-md">
                  <span className="text-[10px] font-mono text-background-50 uppercase tracking-wider">{t('home.generic.badgeTrained')}</span>
                </div>
              </div>
            </div>

            {/* Right: Comparison diagram */}
            <div
              className={`lg:col-span-3 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: '0.3s' }}
            >
              <ComparisonDiagram className="mb-8" />

              {/* Closing statement */}
              <div
                className={`p-6 rounded-xl border-l-4 border-accent-400 bg-accent-50/30 transition-all duration-1000 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: '0.8s' }}
              >
                <p className="text-lg md:text-xl font-heading font-medium text-foreground-800 leading-relaxed">
                  {t('home.generic.statement1')}{' '}
                  <span className="text-accent-500">{t('home.generic.statement2')}</span>
                </p>
              </div>

              {/* Key differentiators */}
              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-shield-keyhole-line', label: t('home.generic.diff.private.label'), desc: t('home.generic.diff.private.desc') },
                  { icon: 'ri-flow-chart', label: t('home.generic.diff.workflows.label'), desc: t('home.generic.diff.workflows.desc') },
                  { icon: 'ri-bar-chart-line', label: t('home.generic.diff.roi.label'), desc: t('home.generic.diff.roi.desc') },
                ].map((item, i) => (
                  <div
                    key={item.label}
                    className={`p-4 rounded-lg bg-background-50 border border-background-200/60 transition-all duration-700 hover:-translate-y-1 hover:shadow-md ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                    style={{ transitionDelay: `${0.6 + i * 0.15}s` }}
                  >
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                      <i className={`${item.icon} text-accent-600`} />
                    </div>
                    <p className="text-sm font-heading font-semibold text-foreground-800 mb-1">{item.label}</p>
                    <p className="text-xs text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}